import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, StateMachine, StateMachineExecution, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService, MonitoringFinding } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { ActivityItem, findingsToActivity, stateMachineRunsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'state-machines' | 'activity';

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['status', 'type', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'     },
  { value: 'name-desc',       label: 'Name Z → A'     },
  { value: 'status-asc',      label: 'Status'         },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'status',
    label: 'Last Execution',
    options: [
      { value: 'SUCCEEDED', label: 'Succeeded' },
      { value: 'FAILED',    label: 'Failed'    },
      { value: 'TIMED_OUT', label: 'Timed Out' },
      { value: 'ABORTED',   label: 'Aborted'   },
      { value: 'RUNNING',   label: 'Running'   },
    ],
  },
  {
    key: 'type',
    label: 'Type',
    options: [
      { value: 'STANDARD', label: 'Standard' },
      { value: 'EXPRESS',  label: 'Express'  },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

const STATUS_ORDER: Record<string, number> = {
  FAILED: 0, TIMED_OUT: 1, ABORTED: 2, RUNNING: 3, SUCCEEDED: 4,
};

@Component({
  selector: 'app-step-functions-detail',
  templateUrl: './step-functions-detail.component.html',
})
export class StepFunctionsDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  stateMachines: StateMachine[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  selectedSm: string | null = null;
  smExecutions: StateMachineExecution[] = [];
  smExecutionsLoading = false;
  smExecutionsError: string | null = null;
  smPage = 1;
  readonly smPageSize = 10;

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',       label: 'Overview'        },
    { id: 'state-machines', label: 'State Machines'  },
    { id: 'activity',       label: 'Activity'        },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'step-functions', name: 'Step Functions', icon: 'git-graph', route: '/cloud/step-functions', status: 'healthy', metrics: [] };

  drawerItemId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;
  private allFindings: MonitoringFinding[] = [];

  private sub?: Subscription;
  private routeSub?: Subscription;
  private smSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('step-functions').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      this.filters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      const sm = (params as Record<string, string>)['sm'] ?? null;
      if (sm !== this.selectedSm) {
        this.selectedSm = sm;
        if (sm) { this.smPage = 1; this.loadSmExecutions(sm); }
        else { this.smSub?.unsubscribe(); this.smExecutions = []; }
      }
    });
    this.loadData(false);
  }

  /** Loads the state-machine list (cache-aware), monitoring state (monitored count), and rebuilds activity. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getStateMachines(bypass).subscribe(sms => {
      this.stateMachines = sms;
      this.loading       = false;
      this.refreshing    = false;
      this.rebuildActivity();
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.allFindings     = findings;
      this.activityLoading = false;
      this.rebuildActivity();
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.smSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  // ── Activity feed ───────────────────────────────────────────────────────────

  private rebuildActivity(): void {
    this.activity = sortActivity([
      ...findingsToActivity(this.allFindings, 'STEP_FUNCTIONS'),
      ...stateMachineRunsToActivity(this.stateMachines),
    ]);
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'state-machines';
    this.selectSm(item.resourceId);
  }

  // ── Summary counts (standardized: total + monitored) ────────────────────────

  get totalCount():     number { return this.stateMachines.length; }
  get monitoredCount(): number { return this.stateMachines.filter(s => this.cloud.isMonitored('step-functions', s.arn)).length; }

  // ── Overview counts (unfiltered) ────────────────────────────────────────────

  get succeededCount(): number { return this.stateMachines.filter(s => s.lastExecutionStatus === 'SUCCEEDED').length; }
  get failedCount():    number { return this.stateMachines.filter(s => s.lastExecutionStatus === 'FAILED' || s.lastExecutionStatus === 'TIMED_OUT' || s.lastExecutionStatus === 'ABORTED').length; }
  get runningCount():   number { return this.stateMachines.filter(s => s.lastExecutionStatus === 'RUNNING').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredStateMachines(): StateMachine[] {
    let result = this.stateMachines;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(s => s.name.toLowerCase().includes(q) || s.arn.toLowerCase().includes(q));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(s => s.lastExecutionStatus && statuses.includes(s.lastExecutionStatus));

    const types = this.filters.facets['type'] ?? [];
    if (types.length) result = result.filter(s => types.includes(s.type));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(s => {
        const mon = this.cloud.isMonitored('step-functions', s.arn);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(sms: StateMachine[], sort: string): StateMachine[] {
    return [...sms].sort((a, b) => {
      switch (sort) {
        case 'name-desc':     return b.name.localeCompare(a.name);
        case 'status-asc':    return (STATUS_ORDER[a.lastExecutionStatus ?? ''] ?? 99) - (STATUS_ORDER[b.lastExecutionStatus ?? ''] ?? 99);
        case 'monitored-first': {
          const aM = this.cloud.isMonitored('step-functions', a.arn) ? 0 : 1;
          const bM = this.cloud.isMonitored('step-functions', b.arn) ? 0 : 1;
          return aM - bM;
        }
        default: return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Drill-down ──────────────────────────────────────────────────────────────

  selectSm(arn: string): void {
    this.smPage = 1;
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { sm: arn },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closeSm(): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { sm: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadSmExecutions(arn: string): void {
    this.smExecutionsLoading = true;
    this.smExecutionsError   = null;
    this.smSub?.unsubscribe();
    this.smSub = this.cloud.getStateMachineExecutions(arn).subscribe({
      next:  execs => { this.smExecutions = execs; this.smExecutionsLoading = false; },
      error: ()    => { this.smExecutionsError = 'Failed to load execution history.'; this.smExecutionsLoading = false; },
    });
  }

  get pagedSmExecutions(): StateMachineExecution[] {
    const start = (this.smPage - 1) * this.smPageSize;
    return this.smExecutions.slice(start, start + this.smPageSize);
  }

  get smPageTotal(): number { return Math.ceil(this.smExecutions.length / this.smPageSize); }

  smExecutionStatusClass(status: string): string {
    const map: Record<string, string> = {
      SUCCEEDED: 'bg-emerald-100 text-emerald-700',
      FAILED:    'bg-red-100 text-red-700',
      TIMED_OUT: 'bg-orange-100 text-orange-700',
      ABORTED:   'bg-amber-100 text-amber-700',
      RUNNING:   'bg-blue-100 text-blue-700',
    };
    return map[status] ?? 'bg-slate-100 text-slate-400';
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(arn: string): boolean { return this.cloud.isMonitored('step-functions', arn); }

  toggleMonitoring(arn: string): void {
    const current = this.cloud.isMonitored('step-functions', arn);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('step-functions', arn, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(arn: string): void  { this.drawerItemId = arn; }
  closeItemSettings(): void            { this.drawerItemId = null; }

  itemOverrideSummary(arn: string): string {
    return this.cloud.getItemOverrideSummary('step-functions', arn);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  executionStatusClass(status: StateMachine['lastExecutionStatus']): string {
    const map: Record<string, string> = {
      SUCCEEDED: 'bg-emerald-100 text-emerald-700',
      FAILED:    'bg-red-100 text-red-700',
      TIMED_OUT: 'bg-orange-100 text-orange-700',
      ABORTED:   'bg-amber-100 text-amber-700',
      RUNNING:   'bg-blue-100 text-blue-700',
    };
    return status ? (map[status] ?? 'bg-slate-100 text-slate-400') : 'bg-slate-100 text-slate-400';
  }

  executionStatusLabel(status: StateMachine['lastExecutionStatus']): string {
    const map: Record<string, string> = {
      SUCCEEDED: 'Succeeded',
      FAILED:    'Failed',
      TIMED_OUT: 'Timed Out',
      ABORTED:   'Aborted',
      RUNNING:   'Running',
    };
    return status ? (map[status] ?? '—') : '—';
  }
}
